/**
 * Settings → Workspace → Attendance — V6-04 §3.
 *
 * The rules that decide everybody's month, in one place, with the shipped
 * defaults visible rather than implied. Nothing here is a second calendar:
 * working days and holidays stay above, and `halfDayWeekdays` sits with them
 * because a half-Saturday is a calendar fact, not an attendance one.
 */

import { useEffect, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { AttendanceRules } from '@virtuetasks/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import { useAttendanceRules, useSaveAttendanceRules } from '@/features/attendance';

/**
 * Full names, not the three-letter form.
 *
 * The working-days control higher up this same page already owns `Mon`…`Sat`
 * as button labels, and two controls answering to the same accessible name on
 * one screen is ambiguous for a screen reader and for anything driving the
 * page. "Saturday" also reads better under "Half working days".
 */
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function AttendanceSettings() {
  const { data: rules, isLoading } = useAttendanceRules();
  const save = useSaveAttendanceRules();
  const [draft, setDraft] = useState<AttendanceRules | null>(null);

  useEffect(() => {
    if (rules) setDraft(rules);
  }, [rules]);

  if (isLoading || !draft) return null;

  function set<K extends keyof AttendanceRules>(key: K, value: AttendanceRules[K]): void {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  }

  async function submit(): Promise<void> {
    try {
      await save.mutateAsync(draft!);
      toast.success('Attendance rules saved');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save the rules');
    }
  }

  return (
    <section className="vt-surface flex flex-col gap-3 p-3" data-testid="attendance-settings">
      <div className="flex items-center gap-2">
        <h2 className="flex-1 text-[13px] font-medium">Attendance</h2>
        <label className="flex items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            data-testid="attendance-enabled"
            checked={draft.enabled}
            onChange={(event) => set('enabled', event.target.checked)}
          />
          Enabled
        </label>
      </div>

      <p className="text-[12px] vt-muted">
        These decide the verdict on everybody's day. Nothing about location is recorded — no GPS, no
        photos, no IP address.
      </p>

      <div className="flex flex-wrap gap-3">
        <Field label="Expected start" htmlFor="expected-start">
          <Input
            id="expected-start"
            type="time"
            data-testid="expected-start"
            value={draft.expectedStart}
            onChange={(event) => set('expectedStart', event.target.value)}
          />
        </Field>
        <Field label="Expected end" htmlFor="expected-end">
          <Input
            id="expected-end"
            type="time"
            value={draft.expectedEnd}
            onChange={(event) => set('expectedEnd', event.target.value)}
          />
        </Field>
        <Field label="Grace (min)" htmlFor="grace">
          <Input
            id="grace"
            type="number"
            min={0}
            data-testid="grace-minutes"
            value={draft.graceMinutes}
            onChange={(event) => set('graceMinutes', Number(event.target.value))}
          />
        </Field>
        <Field label="Full day (min)" htmlFor="full-day">
          <Input
            id="full-day"
            type="number"
            min={1}
            data-testid="full-day-minutes"
            value={draft.fullDayMinutes}
            onChange={(event) => set('fullDayMinutes', Number(event.target.value))}
          />
        </Field>
        <Field label="Half day (min)" htmlFor="half-day">
          <Input
            id="half-day"
            type="number"
            min={1}
            data-testid="half-day-minutes"
            value={draft.halfDayMinutes}
            onChange={(event) => set('halfDayMinutes', Number(event.target.value))}
          />
        </Field>
        <Field label="Auto-close at" htmlFor="auto-close">
          <Input
            id="auto-close"
            type="time"
            data-testid="auto-close-at"
            value={draft.autoCloseAt}
            onChange={(event) => set('autoCloseAt', event.target.value)}
          />
        </Field>
        <Field label="Auto-close grace (min)" htmlFor="auto-close-grace">
          <Input
            id="auto-close-grace"
            type="number"
            min={0}
            value={draft.autoCloseGraceMinutes}
            onChange={(event) => set('autoCloseGraceMinutes', Number(event.target.value))}
          />
        </Field>
      </div>

      <div className="flex flex-col gap-1">
        <Label>Half working days</Label>
        <p className="text-[11px] vt-muted">
          Days that count as working days at half the expected hours — the half-Saturday case. The
          full-day and half-day thresholds halve on these.
        </p>
        <div className="flex flex-wrap gap-1">
          {WEEKDAYS.map((name, index) => {
            const on = draft.halfDayWeekdays.includes(index);
            return (
              <Button
                key={name}
                variant={on ? 'subtle' : 'ghost'}
                size="sm"
                aria-pressed={on}
                data-testid={`half-day-${index}`}
                onClick={() =>
                  set(
                    'halfDayWeekdays',
                    on
                      ? draft.halfDayWeekdays.filter((day) => day !== index)
                      : [...draft.halfDayWeekdays, index],
                  )
                }
              >
                {name}
              </Button>
            );
          })}
        </div>
      </div>

      <label className="flex items-center gap-2 text-[13px]">
        <input
          type="checkbox"
          data-testid="attendance-reminders"
          checked={draft.remindersEnabled}
          onChange={(event) => set('remindersEnabled', event.target.checked)}
        />
        Send the two daily reminders (not started, still running)
      </label>

      <Button
        variant="primary"
        size="sm"
        className="self-start"
        data-testid="save-attendance-rules"
        disabled={save.isPending}
        onClick={() => void submit()}
      >
        {save.isPending ? <Loader2 className="animate-spin" /> : <Check />}
        Save attendance rules
      </Button>
    </section>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  );
}
